/* NI shared quick-buy. Option state comes from Shopify; no product-wide variant JSON. */
(() => {
  if (customElements.get('ni-product-card')) return;
  let sequence = 0;
  class NIProductCard extends HTMLElement {
    constructor() {
      super();
      this.uid = `ni-card-${++sequence}`;
      this.requestNumber = 0;
      this.loading = false;
      this.failed = false;
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
    }
    connectedCallback() {
      if (!this.bound) {
        this.addEventListener('change', this.onChange);
        this.addEventListener('submit', this.onSubmit, true);
        this.addEventListener('click', this.onClick);
        this.bound = true;
      }
      this.prepare();
    }
    disconnectedCallback() { this.controller?.abort(); }
    prepare() {
      this.querySelectorAll('[data-ni-id]').forEach(el => { el.id = `${this.uid}-${el.dataset.niId}`; });
      this.querySelectorAll('[data-ni-for]').forEach(el => { el.htmlFor = `${this.uid}-${el.dataset.niFor}`; });
      this.querySelectorAll('[data-ni-option]').forEach(el => { el.disabled = false; });
      const submit = this.querySelector('[data-ni-submit]');
      const state = this.querySelector('[data-ni-available]');
      if (submit) submit.disabled = this.loading || this.failed || state?.dataset.niAvailable !== 'true';
    }
    message(text, retry = false) {
      const box = this.querySelector('[data-ni-message]');
      if (box) { box.textContent = text; box.hidden = !text; }
      const button = this.querySelector('[data-ni-retry]');
      if (button) button.hidden = !retry;
    }
    onClick(event) {
      if (event.target.closest('[data-ni-retry]')) { event.preventDefault(); this.updateOptions(); }
    }
    onChange(event) {
      if (event.target.matches('[data-ni-option]')) this.updateOptions(event.target.dataset.niOption);
      if (event.target.matches('[data-ni-quantity]')) event.target.setCustomValidity('');
    }
    onSubmit(event) {
      const form = event.target;
      if (!form.matches('[data-ni-form]')) return;
      const state = this.querySelector('[data-ni-available]');
      const variant = form.querySelector('[name="id"]');
      const qty = form.querySelector('[name="quantity"]');
      const blocked = this.loading || this.failed || state?.dataset.niAvailable !== 'true' || !variant?.value;
      if (qty) {
        qty.setCustomValidity('');
        if (!Number.isInteger(Number(qty.value)) || Number(qty.value) < 1) qty.setCustomValidity('Veldu heilan fjölda, að minnsta kosti 1.');
      }
      if (blocked || !form.checkValidity()) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (blocked) this.message(this.loading ? 'Bíddu augnablik á meðan valið er uppfært.' : 'Veldu fáanlega útfærslu áður en þú bætir í körfu.', this.failed);
        else form.reportValidity();
      }
    }
    async updateOptions(focusPosition) {
      this.controller?.abort();
      const controller = this.controller = new AbortController();
      const number = ++this.requestNumber;
      const selects = [...this.querySelectorAll('[data-ni-option]')];
      const ids = selects.map(el => el.value);
      if (!ids.length) return;
      const changed = selects.find(el => el.dataset.niOption === focusPosition);
      const choice = changed?.selectedOptions[0];
      let url;
      try {
        url = new URL(choice?.dataset.productUrl || this.dataset.productUrl, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('External option URL');
      } catch (error) {
        this.loading = false; this.failed = true; this.removeAttribute('aria-busy'); this.prepare();
        this.message('Opnaðu vörusíðuna til að velja þessa útfærslu.'); return;
      }
      const sameProduct = new URL(this.dataset.productUrl, window.location.origin).pathname === url.pathname;
      url.searchParams.delete('variant');
      url.searchParams.set('section_id', 'ni-card-state');
      if (sameProduct) url.searchParams.set('option_values', ids.join(','));
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      this.loading = true; this.failed = false;
      this.setAttribute('aria-busy', 'true'); this.prepare(); this.message('Uppfæri val…');
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url.toString(), { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        if (number !== this.requestNumber || !this.isConnected) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('ni-product-card');
        if (!next || !next.querySelector('[data-ni-available]')) throw new Error('Missing card state');
        const returned = [...next.querySelectorAll('[data-ni-option]')].map(el => el.value);
        if (sameProduct && returned.join(',') !== ids.join(',')) throw new Error('Option state mismatch');
        const oldQty = this.querySelector('[data-ni-quantity]')?.value;
        const focused = document.activeElement;
        const focusKey = this.contains(focused) ? focused?.dataset.niId : null;
        this.innerHTML = next.innerHTML;
        this.dataset.productUrl = next.dataset.productUrl;
        this.dataset.productId = next.dataset.productId;
        const oldView = this.closest('product-component');
        const newView = next.closest('product-component');
        if (oldView && newView) oldView.setAttribute('view-event-payload', newView.getAttribute('view-event-payload') || '');
        const qty = this.querySelector('[data-ni-quantity]');
        if (qty) {
          const min = Number(qty.min) || 1, step = Number(qty.step) || 1;
          let value = Math.max(min, Number(oldQty) || min);
          if (qty.hasAttribute('max')) value = Math.min(value, Number(qty.max));
          qty.value = String(Math.max(min, min + Math.floor((value - min) / step) * step));
        }
        this.loading = false; this.failed = false; this.prepare();
        if (focusKey) [...this.querySelectorAll('[data-ni-id]')].find(el => el.dataset.niId === focusKey)?.focus({ preventScroll: true });
      } catch (error) {
        if (number !== this.requestNumber || !this.isConnected) return;
        this.failed = true;
        this.message('Ekki tókst að sækja valið. Reyndu aftur eða opnaðu vörusíðuna.', true);
      } finally {
        clearTimeout(timeout);
        if (number === this.requestNumber) { this.loading = false; this.removeAttribute('aria-busy'); this.prepare(); }
      }
    }
  }
  customElements.define('ni-product-card', NIProductCard);
})();
